import { Component, Input, OnInit } from '@angular/core';
import { ProformViewStates } from '../../../dtos/proforms.dto';
import { ProformsSubmissionService } from '../../../services/proforms/proforms-submission.service';
import { CreateProformSubmissionPayload } from '../../../types/payloads/proforms.payloads';
import {
  ProFormsQuestionAPIResponse,
  ProFormsSubmissionAPIResponse,
  RubricFieldTypeSpecs,
} from '../../../types/responses/proforms.responses';

@Component({
  selector: 'app-proforms-question-answer-rubric',
  standalone: false,
  templateUrl: './proforms-question-answer-rubric.component.html',
  styleUrl: './proforms-question-answer-rubric.component.scss',
})
export class ProformsQuestionAnswerRubricComponent implements OnInit {
  @Input() question: ProFormsQuestionAPIResponse;

  @Input() viewState: ProformViewStates;

  fieldTypeSpecs: RubricFieldTypeSpecs;

  viewStates = ProformViewStates;

  chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  constructor(private proformsSubmissionService: ProformsSubmissionService) {}

  ngOnInit(): void {
    if (this.question) {
      this.fieldTypeSpecs = JSON.parse(this.question.field_type_specs);
      if (
        this.viewState === this.viewStates.SUBMITTED ||
        this.viewState === this.viewStates.GRADED
      ) {
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            setTimeout(() => {
              this.setAnswersFromSubmission(submission);
            }, 100);
          }
        );
      } else if (this.viewState === this.viewStates.EDIT) {
        // Pre-populate answers if they exist (coach is viewing an already submitted observation form)
        this.proformsSubmissionService.currentSubmission.subscribe(
          (submission) => {
            if (submission?.answers && submission.answers.length > 0) {
              // Use currentSubmission if it has answers
              setTimeout(() => {
                this.setAnswersFromSubmission(submission);
              }, 100);
            }
          }
        );

        this.proformsSubmissionService.newSubmission.subscribe((submission) => {
          setTimeout(() => {
            this.setAnswersFromSubmission(submission);
          }, 100);
        });
      }
    }
  }

  get isInputInteractive(): boolean {
    return this.viewState === this.viewStates.EDIT;
  }

  setAnswersFromSubmission(
    submission:
      | CreateProformSubmissionPayload
      | ProFormsSubmissionAPIResponse
      | null
  ): void {
    // Clear any existing selections for this question before
    // applying the latest submission state
    const allLevelItems = document.querySelectorAll('.level-item');
    allLevelItems.forEach((item) => {
      const hasQuestionClass = Array.from(item.classList).some((cls) =>
        cls.startsWith(`${this.question.id}_comp_`)
      );
      if (hasQuestionClass) {
        item.classList.remove('selected');
      }
    });

    if (!submission?.answers) {
      return;
    }

    // FIND RELEVANT ANSWERS
    const currentQuestionsAnswers = submission.answers.filter(
      (a) => a.proform_form_question_id === this.question.id
    );

    if (currentQuestionsAnswers.length === 0) {
      return;
    }

    // IF OPTION EXISTS, APPLY THE "SELECTED" CLASS
    currentQuestionsAnswers.forEach((answer) => {
      const option = this.question.answerOptions.find(
        (currentOption) =>
          currentOption.id === answer.proform_form_question_answer_option_id
      );
      if (option) {
        let ciIndex: number; // competency index as number
        let giLevel: number;

        if (answer.selectedCell) {
          const parts = answer.selectedCell.split(':');
          ciIndex = parseInt(parts[0], 10);
          giLevel = parseInt(parts[1], 10) + 1;
        } else if (option.answer) {
          // Fallback: derive from option.answer when selectedCell is not available
          const ciChar = option.answer.split(':')[0];
          ciIndex = this.chars.indexOf(ciChar);
          giLevel = parseInt(option.answer.split(':')[1], 10);
        } else {
          return;
        }

        const elementId = `${this.question.id}_comp_${ciIndex}_lvl_${giLevel}`;

        const target = document.getElementById(elementId) as HTMLInputElement;

        if (target) {
          target.classList.add('selected');
        }
      }
    });
  }

  selectLevel(ci: number, gi_level: number, event: Event) {
    if (this.viewState !== this.viewStates.EDIT) return;

    // REMOVE AND RE-APPLY "SELECTED" CLASS
    const allLevelItems = document.querySelectorAll('.level-item');
    allLevelItems.forEach((item) => {
      if (item.classList.contains(`${this.question.id}_comp_${ci}`)) {
        item.classList.remove('selected');
      }
    });
    const input = event.target as HTMLInputElement;
    input.parentElement?.classList.add('selected');

    // FIND MATCHING OPTION
    const optionAnswer = `${this.chars[ci]}:${gi_level}`;
    const option = this.question.answerOptions.find(
      (answerOption) => optionAnswer === answerOption.answer
    );

    // FIND EXISTING OPTIONS FOR COMPETENCY AND REMOVE FROM PAYLOAD
    const otherOptions = this.question.answerOptions.filter(
      (answerOption) => answerOption.answer.split(':')[0] === this.chars[ci]
    );
    otherOptions.forEach((otherOption) => {
      this.proformsSubmissionService.removeAnswerFromPayload(otherOption.id);
    });

    // UPDATE SERVICE
    if (option) {
      this.proformsSubmissionService.addAnswerToPayload(
        {
          proform_form_question_id: this.question.id,
          proform_form_question_answer_option_id: option?.id,
        },
        this.question.field_type
      );
    }
  }
}
